import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TrialItemTemplateCategoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsTrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as CategoriesActions from "../../../actions/categories";
import * as NavigationActions from "../../../actions/navigation";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialItemTemplateCategoriesPage.scss";

interface TrialItemTemplateCategorysPageProps {
    formFilter: IFormFilter;
    trialItemTemplateCategory: Dtos.Category[];
    loadingTrialItemTemplateCategorys: boolean;
    loadTrialItemTemplateCategorysSuccess: boolean;
    loadTrialItemTemplateCategorysFailure: boolean;
    loadTrialItemTemplateCategorysErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialItemTemplateCategorys: CategoriesActions.ICategoryClearActionCreator;
    loadTrialItemTemplateCategorys: CategoriesActions.ICategoryLoadActionCreator;
    filter: CategoriesActions.ICategoryFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialItemTemplateCategorysPage: FC<
    TrialItemTemplateCategorysPageProps
> = ({
    formFilter,
    trialItemTemplateCategory,
    loadingTrialItemTemplateCategorys,
    loadTrialItemTemplateCategorysSuccess,
    loadTrialItemTemplateCategorysFailure,
    loadTrialItemTemplateCategorysErrorCode,
    permissions,
    clearTrialItemTemplateCategorys,
    loadTrialItemTemplateCategorys,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showTrialItemTemplateCategorys =
        !loadingTrialItemTemplateCategorys &&
        loadTrialItemTemplateCategorysSuccess;


    // Load trial item template categories
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(TrialItemTemplateCategoriesBreadcrumbs());

            loadTrialItemTemplateCategorys();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialItemTemplateCategoriesBreadcrumbs,
        loadTrialItemTemplateCategorys,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialItemTemplateCategorysFailure) {
            navigate(`/error/${loadTrialItemTemplateCategorysErrorCode}`);
        }
    }, [
        loadTrialItemTemplateCategorysFailure,
        loadTrialItemTemplateCategorysErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the trial item template categories on component unmount
    useEffect(() => {
        return () => {
            clearTrialItemTemplateCategorys();
        };
    }, [clearTrialItemTemplateCategorys]);

    const createTable = useMemo(() => {
        if (
            !trialItemTemplateCategory ||
            trialItemTemplateCategory.length == 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no item categories to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            trialItemTemplateCategory.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialItemTemplateCategory}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : trialItemTemplateCategory.length
                    }
                    pageSize={
                        showPagination
                            ? undefined
                            : trialItemTemplateCategory.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no item categories to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/trial/itemtemplatecategories/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialItemTemplateCategory,
        DEFAULT_PAGE_SIZE,
        PAGE_SIZE_OPTIONS,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Trial Item Categories</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.CategoriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/trial/itemtemplatecategories/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialItemTemplateCategorys ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialItemTemplateCategorys ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showTrialItemTemplateCategorys,
        createTable,
    ]);

    return (
        <TrialLookupsLayout
            loading={loadingTrialItemTemplateCategorys}
            permission={Dtos.Permission.CategoriesView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialItemTemplateCategorysPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    return {
        formFilter: state.categories.formFilter,
        trialItemTemplateCategory: state.categories.formFilteredData,
        loadingTrialItemTemplateCategorys:
            state.categories.loadState &&
            state.categories.loadState.status == RequestState.Pending,
        loadTrialItemTemplateCategorysSuccess:
            state.categories.loadState &&
            state.categories.loadState.status == RequestState.Success,
        loadTrialItemTemplateCategorysFailure:
            state.categories.loadState &&
            state.categories.loadState.status == RequestState.Failure,
        loadTrialItemTemplateCategorysErrorCode:
            state.categories.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialItemTemplateCategorysPageProps> => {
    return {
        loadTrialItemTemplateCategorys: bindActionCreators(
            CategoriesActions.LoadCategory,
            dispatch
        ),
        clearTrialItemTemplateCategorys: bindActionCreators(
            CategoriesActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(CategoriesActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialItemTemplateCategorysPage);
